import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * §9-Styleguide Status-Pill — Outline-Style (nicht filled).
 * Jeder Variante hat eigene Border + Text-Farbe, kleiner Dot vorne.
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1.5 border px-2 py-[2px] text-[11px] font-medium',
  {
    variants: {
      variant: {
        default: 'border-avameo-primary/70 text-avameo-primary bg-transparent',
        secondary: 'border-hair-strong text-ink-soft bg-transparent',
        destructive: 'border-danger/70 text-danger bg-transparent',
        outline: 'border-hair-strong text-ink-muted bg-transparent',
        success: 'border-success/70 text-success bg-transparent',
        warning: 'border-warning/70 text-warning bg-transparent',
        info: 'border-info/70 text-info bg-transparent',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

const dotColors: Record<string, string> = {
  default: 'bg-avameo-primary',
  secondary: 'bg-ink-soft',
  destructive: 'bg-danger',
  outline: 'bg-ink-muted',
  success: 'bg-success',
  warning: 'bg-warning',
  info: 'bg-info',
};

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  hideDot?: boolean;
}

export function Badge({ className, variant = 'default', hideDot = false, children, ...props }: BadgeProps) {
  const key = variant ?? 'default';
  const dot = dotColors[key] ?? 'bg-avameo-primary';
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props}>
      {!hideDot && <span className={cn('h-[5px] w-[5px] shrink-0', dot)} />}
      {children}
    </div>
  );
}
